import type { LocationsResponse } from '@/src/types/apps/gygWasteLogging'

/**
 * `_metadata` on CrunchTime BFF responses (Nest `AuditableResponse`).
 */
export type CrunchTimeAuditMetadata = {
  entitySource?: string
  entitySourceId?: number
  description?: string
}

/** After unwrapping global + auditable layers. */
export type CrunchTimeParsedResponse<T> = {
  payload: T
  metadata?: CrunchTimeAuditMetadata
}

/**
 * HTTP body on axios `response.data`: global `{ data: controllerReturn }`.
 * Peel only when this object is not already `{ data, _metadata }` (auditable layer).
 */
const unwrapGlobalDataWrapper = (axiosRootData: unknown): unknown => {
  if (axiosRootData === undefined || axiosRootData === null) {
    return axiosRootData
  }
  if (typeof axiosRootData !== 'object' || Array.isArray(axiosRootData)) {
    return axiosRootData
  }
  const o = axiosRootData as Record<string, unknown>
  if (Object.prototype.hasOwnProperty.call(o, 'data') && !Object.prototype.hasOwnProperty.call(o, '_metadata')) {
    return o.data
  }

  return axiosRootData
}

/**
 * Controller return: `{ data: T, _metadata }` or test-connection `{ connected, _metadata }` (no inner `data`).
 */
const splitAuditableInner = (inner: unknown): CrunchTimeParsedResponse<unknown> => {
  if (inner === undefined || inner === null || typeof inner !== 'object' || Array.isArray(inner)) {
    return { payload: inner }
  }

  const o = inner as Record<string, unknown>
  if (!Object.prototype.hasOwnProperty.call(o, '_metadata')) {
    return { payload: inner }
  }

  const metadata = o._metadata as CrunchTimeAuditMetadata | undefined

  if (Object.prototype.hasOwnProperty.call(o, 'data')) {
    return { payload: o.data, metadata }
  }

  const rest = { ...o }
  delete rest._metadata

  return { payload: rest, metadata }
}

/**
 * Normalizes CrunchTime GET responses after global wrap + optional AuditableResponse.
 *
 * Supports:
 * - `{ data: { data: T, _metadata } }` (auditable)
 * - `{ data: { connected, _metadata } }` (test-connection, flat)
 * - `{ data: { connected } }` (pre-audit)
 */
export const parseCrunchTimeAxiosResponse = <T = unknown>(
  axiosResponse: { data?: unknown } | undefined
): CrunchTimeParsedResponse<T> => {
  const inner = unwrapGlobalDataWrapper(axiosResponse?.data)
  const { payload, metadata } = splitAuditableInner(inner)

  return { payload: payload as T, metadata }
}

/** GET /crunchtime/test-connection — works for flat `{ connected, _metadata }` and legacy `{ data: { connected }, _metadata }`. */
export const parseCrunchTimeTestConnection = (
  axiosResponse: { data?: unknown } | undefined
): {
  connected: boolean
  metadata?: CrunchTimeAuditMetadata
} => {
  const { payload, metadata } = parseCrunchTimeAxiosResponse<{ connected?: boolean }>(axiosResponse)
  const p = payload as Record<string, unknown> | null | undefined
  const raw = p?.connected
  const connected = raw === true || raw === 'true'

  return { connected, metadata }
}

function unwrapNestedListEnvelope(inner: unknown): unknown {
  if (inner === null || inner === undefined || typeof inner !== 'object' || Array.isArray(inner)) {
    return inner
  }
  const shell = inner as Record<string, unknown>
  const nested = shell.data
  if (
    nested &&
    typeof nested === 'object' &&
    !Array.isArray(nested) &&
    Object.prototype.hasOwnProperty.call(nested, 'success') &&
    Object.prototype.hasOwnProperty.call(nested, 'data') &&
    Array.isArray((nested as { data?: unknown }).data)
  ) {
    return nested
  }

  return inner
}

/**
 * Normalizes list-shaped CrunchTime payloads. When the API sends `{ success, count, data }`,
 * preserves explicit `success: false`; defaults to `success: true` only if `success` is not a boolean.
 * Bare arrays are coerced to a success envelope (no upstream flag). The `empty` argument is returned
 * as-is when the shape does not match — including its `success` value.
 */
export const coerceCrunchTimeListEnvelope = <T>(
  inner: unknown,
  empty: { success: boolean; count: number; data: T[] }
): { success: boolean; count: number; data: T[] } => {
  const node = unwrapNestedListEnvelope(inner)

  if (Array.isArray(node)) {
    return { success: true, count: node.length, data: node as T[] }
  }
  const o = node as { success?: boolean; count?: number; data?: T[] } | null | undefined
  if (o && Array.isArray(o.data)) {
    return {
      success: typeof o.success === 'boolean' ? o.success : true,
      count: o.count ?? o.data.length,
      data: o.data
    }
  }

  return empty
}

export const coerceCrunchTimeLocationsResponse = (inner: unknown): LocationsResponse => {
  const node = unwrapNestedListEnvelope(inner)

  if (Array.isArray(node)) {
    return { success: true, count: node.length, data: node, filter: null }
  }
  const b = node as Partial<LocationsResponse> | null | undefined
  if (b && Array.isArray(b.data)) {
    return {
      success: b.success ?? true,
      count: b.count ?? b.data.length,
      data: b.data,
      filter: b.filter ?? null
    }
  }

  return { success: true, count: 0, data: [], filter: null }
}
